import * as React from "react";
import ReactCrop, {Crop} from "react-image-crop";

interface ImageWidgetProps {
    title: string;
    altText: string;
    src: string;
    canCrop: boolean;
    onSave: Function;
    onCropStart: Function;
    onCropEnd: Function;
    originalImageSceneRelativeSizes: {
        width: number,
        height: number
    };
    crop: {
        x: number,
        y: number
    };
}

interface ImageWidgetState {
    cropState: Crop;
    image?: {
        width: number,
        height: number
    };
    isCropping: boolean;
    isLoading: boolean;
}

// The class for ReactCrop doesn't include this as part of it's properties.
interface ReactCropRef extends ReactCrop {
    imageRef: HTMLImageElement;
}

const LoadingIndicator = () => (<div className="loader">Loading...</div>);

class ImageWidgetComponent extends React.Component<ImageWidgetProps, ImageWidgetState> {
    protected reactCropRef = React.createRef<ReactCropRef>();

    constructor (props: ImageWidgetProps) {
        super(props);

        this.state = {
            cropState: {
                x: 0,
                y: 0,
                width: 0,
                height: 0,
            },
            isCropping: false,
            isLoading: true,
        };

        this.reactCropRef = React.createRef();
    }

    calculateCropCoords = ():{
        prevX: number, prevY: number
    } => {
        let prevX = 0;
        let prevY = 0;
        const { originalImageSceneRelativeSizes, crop } = this.props;
        if (originalImageSceneRelativeSizes) {
            let onePercentOriginalImageWidth = (originalImageSceneRelativeSizes.width / 100);
            let onePercentOriginalImageHeight = (originalImageSceneRelativeSizes.height / 100);

            prevX = onePercentOriginalImageWidth * (crop.x * 100);
            prevY = onePercentOriginalImageHeight * (crop.y * 100);
        }

        return {prevX, prevY};
    }

    handleCropStart = () => {
        const {imageRef} = this.reactCropRef.current;
        // Calculate select position of cropper
        const {prevX, prevY} = this.calculateCropCoords();
        this.setState({
            isCropping: true,
            cropState: {
                x: prevX, // Previous crop coordinates
                y: prevY, // Previous crop coordinates
                width: imageRef.width,
                height: imageRef.height,
            },
            image: {
                width: imageRef.width,
                height: imageRef.height
            }
        });
        this.props.onCropStart();
    }

    handleCropStop = () => {
        const { onCropEnd } = this.props;
        this.setState({isCropping: false});
        onCropEnd();
    }

    handleChange = (cropState: Crop) => {
        // Sometimes the *react-image-crop* returns
        // an exponential value instead a decimal
        // ex: 5.684341886080802e-14
        // Will prevent errors, by converting them
        // to correct decimal values
        const {x, y, width, height} = cropState;
        this.setState({
            cropState: {
                x: parseFloat(x.toFixed(3)),
                y: parseFloat(y.toFixed(3)),
                height: parseFloat(height.toFixed(3)),
                width: parseFloat(width.toFixed(3)),
            }
        });
    }

    handleSave = () => {
        const {onSave, canCrop, crop} = this.props;
        // Double checking to make sure we're allowed to crop and in turn save.
        if (canCrop) {
            const {image, cropState} = this.state;
            const {imageRef} = this.reactCropRef.current;
            const {x, y, width, height} = cropState;
            const percentageCrop = {
                x: x / imageRef.width,
                y: y / imageRef.height,
                width: width / (crop ? imageRef.width : image.width),
                height: height / (crop ? imageRef.height : image.height)
            };

            const hasChanges =
                percentageCrop.x >= 0 || percentageCrop.y >= 0 ||
                percentageCrop.width <= 1 || percentageCrop.height <= 1;

            const isValid = width !== 0 || height !== 0;

            if(hasChanges && isValid) {
                onSave(percentageCrop);
                this.setState({isLoading: true});
            }
        }

        this.handleCropStop();
    }

    onImageLoaded = () => {
        this.setState({isLoading: false});
    }

    onKeyPress = (event: React.KeyboardEvent) => {
        if (event.keyCode === 27) {
            // Handle esc key
            this.handleCropStop();
        } else if (event.keyCode === 13) {
            // Handle enter key
            this.handleSave();
        }
    }

    renderImage = () => {
        const { src, canCrop } = this.props;
        const { cropState, isCropping, isLoading } = this.state;

        // Feeding an invalid value for crop when we're not cropping.
        const targetCrop = isCropping ? cropState : {};

        const style: React.CSSProperties = !isCropping ? {cursor: "inherit"}: {};
        style.display = isLoading ? "none" : "";

        return (
            <>
                {isLoading && <LoadingIndicator/>}
                <ReactCrop
                    ref={this.reactCropRef}
                    src={src}
                    disabled={!canCrop}
                    locked={!canCrop}
                    // On change is required as we must track the cropping state.
                    onChange={this.handleChange}
                    crop={targetCrop}
                    onImageLoaded={this.onImageLoaded}
                    style={style}
                />
            </>
        );
    }

    renderCropIcons = () => {
        const { canCrop } = this.props;
        const { isCropping, isLoading } = this.state;

        if(canCrop && !isLoading) {
            if (isCropping) {
                return (
                    <>
                        {/* TODO: Use glyphicon component once its been merged. */}
                        <i className="image-crop image-save-crop glyphicon glyphicon-ok" onClick={this.handleSave}/>
                        <i
                            className="image-crop image-disable-crop glyphicon glyphicon-remove"
                            onClick={this.handleCropStop}
                        />
                    </>);
            }

            return <div className="image-crop-button" onClick={this.handleCropStart}/>;
        }
    }

    render () {
        return (
            <div className="image-widget-component-container" onKeyDown={this.onKeyPress}>
                {this.renderImage()}
                {this.renderCropIcons()}
            </div>
        );
    }
}

export {
    ImageWidgetComponent as default,
    ImageWidgetComponent,
};



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/imageWidget/ImageWidgetComponent.tsx